<template>
  <div class="structural-chart">
    <div class="structural-head">
      <h3>Performance Estrutural</h3>
      <div class="structural-tabs" role="tablist">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.id"
          :class="{ active: activeTab === tab.id }"
          @click="activeTab = tab.id"
        >{{ tab.name }}</button>
        <strong v-if="lastLabel" class="structural-last" :style="{ color: active.color }">{{ lastLabel }}</strong>
      </div>
    </div>
    <div v-if="points.length" class="structural-plot">
      <apexchart
        :key="chartKey"
        type="line"
        height="100%"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
    <p v-else class="structural-empty">Nenhum evento do período com snapshot.</p>
  </div>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { getLogTimestamp } from '@/utils/aggregateLogs.js';
import { buildEdgesSeries, sampleEdgesSeries } from '@/utils/computeEdgesSeries.js';

const TABS = [
  { id: 'locked', name: 'Locked Basis', color: '#a78bfa', field: 'lockedBasisPnl' },
  { id: 'structural', name: 'Estrutural', color: '#38bdf8', field: 'structuralPnl' },
  { id: 'net', name: 'Líquido', color: '#f59e0b', field: 'structuralDelta' },
];

function formatSignedUsd(val, withPlus = false) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (n < 0) return `-$${abs}`;
  return withPlus && n > 0 ? `+$${abs}` : `$${abs}`;
}

function formatWhen(ts) {
  if (!Number.isFinite(ts)) return '';
  return new Date(ts).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function lineOptions(color, seriesName, lastIndex = -1) {
  const markLast = lastIndex >= 0;
  return {
    ...defaultChartOptions,
    colors: [color],
    legend: { show: false },
    stroke: { curve: 'straight', width: 2 },
    markers: {
      size: 3,
      hover: { size: 4 },
      strokeWidth: 0,
      discrete: markLast ? [{
        seriesIndex: 0,
        dataPointIndex: lastIndex,
        size: 7,
        fillColor: color,
        strokeColor: '#fff',
        strokeWidth: 2,
      }] : [],
    },
    dataLabels: {
      enabled: markLast,
      offsetX: -6,
      offsetY: -10,
      formatter(val, opts) {
        if (opts.dataPointIndex !== lastIndex) return '';
        return formatSignedUsd(val, true);
      },
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [color],
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        borderRadius: 4,
        padding: 4,
        borderWidth: 0,
        opacity: 0.85,
        dropShadow: { enabled: false },
      },
    },
    grid: {
      ...defaultChartOptions.grid,
      padding: { top: markLast ? 20 : 0, right: markLast ? 36 : 8 },
    },
    xaxis: {
      type: 'datetime',
      labels: { datetimeUTC: false },
    },
    yaxis: {
      labels: { formatter: (val) => formatSignedUsd(val) },
    },
    tooltip: {
      shared: false,
      intersect: false,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const value = series?.[seriesIndex]?.[dataPointIndex];
        const ts = w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex];
        const isLast = dataPointIndex === lastIndex;
        return `<div style="background:#1e1e1e;color:#fff;padding:8px 10px;border:1px solid #444;border-radius:6px;font-size:12px;">
          <div style="opacity:.75;margin-bottom:4px;">${formatWhen(ts)}${isLast ? ' · último' : ''}</div>
          <div><b style="color:${color}">${seriesName}</b> ${formatSignedUsd(value, true)}</div>
        </div>`;
      },
    },
  };
}

export default {
  name: 'EvolStructuralChart',
  props: {
    rawData: { type: Array, default: () => [] },
    aggregatedData: { type: Array, default: () => [] },
  },
  data() {
    return { activeTab: 'net', tabs: TABS };
  },
  computed: {
    active() {
      return TABS.find((tab) => tab.id === this.activeTab) || TABS[2];
    },
    points() {
      const series = buildEdgesSeries(this.rawData);
      const marks = (this.aggregatedData || [])
        .map((log) => getLogTimestamp(log))
        .filter((ts) => Number.isFinite(ts));
      return sampleEdgesSeries(series, marks);
    },
    chartKey() {
      const last = this.points[this.points.length - 1];
      return `${this.activeTab}-${this.points.length}-${last ? last.plotTs : 0}`;
    },
    chartSeries() {
      const { field, name } = this.active;
      return [{
        name,
        data: this.points.map((point) => [point.plotTs, Number(point[field].toFixed(2))]),
      }];
    },
    lastLabel() {
      const last = this.points[this.points.length - 1];
      if (!last) return '';
      return formatSignedUsd(last[this.active.field], true);
    },
    chartOptions() {
      const { color, name } = this.active;
      return lineOptions(color, name, this.points.length - 1);
    },
  },
};
</script>

<style scoped>
.structural-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: var(--table-header-bgcolor);
  border-radius: 10px;
  padding: 16px 16px 8px;
}

.structural-head h3 {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #f1f1f1;
}

.structural-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.structural-last {
  font-size: 15px;
  font-weight: 700;
}

.structural-tabs button {
  padding: 6px 18px;
  border-radius: 8px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-muted, #9ca3af);
  font-size: 13px;
  cursor: pointer;
}

.structural-tabs button.active {
  border-color: #6366f1;
  background: #1e1e2e;
  color: #a5b4fc;
  font-weight: 600;
}

.structural-plot {
  height: calc(100% - 88px);
}

.structural-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
